import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ArrowLeft, X } from "lucide-react";
import HamburgerMenu from "@/components/HamburgerMenu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function CountryAutocomplete({ countries, value, onPick, onClear }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return countries.slice(0, 8);
        return countries
            .filter(
                (c) =>
                    c.country_name.toLowerCase().includes(q) ||
                    c.country_code.toLowerCase().includes(q),
            )
            .slice(0, 8);
    }, [query, countries]);

    const selected = countries.find((c) => c.country_code === value);

    return (
        <div className="relative" data-testid="blog-country-search">
            <div className="flex items-center gap-2 rounded-xl border border-[#E5E5E5] bg-white px-4 py-2.5 transition focus-within:border-[#0A0A0A]">
                <Search size={15} strokeWidth={2} className="text-[#737373]" />
                <input
                    type="text"
                    value={open ? query : selected ? `${selected.country_flag} ${selected.country_name}` : query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (!open) setOpen(true);
                    }}
                    onFocus={() => {
                        setOpen(true);
                        if (selected) setQuery("");
                    }}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    placeholder={t("blog.search_country")}
                    data-testid="blog-country-input"
                    className="w-full bg-transparent text-[14px] text-[#0A0A0A] placeholder:text-[#A3A3A3] focus:outline-none"
                />
                {selected && (
                    <button
                        type="button"
                        onClick={() => {
                            setQuery("");
                            onClear();
                        }}
                        data-testid="blog-country-clear"
                        className="rounded-full p-1 text-[#737373] transition hover:bg-[#F5F5F5]"
                        aria-label="Clear country filter"
                    >
                        <X size={13} strokeWidth={2} />
                    </button>
                )}
            </div>
            {open && filtered.length > 0 && (
                <ul className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-[0_8px_24px_rgba(10,10,10,0.06)]">
                    {filtered.map((c) => (
                        <li key={c.country_code}>
                            <button
                                type="button"
                                onClick={() => {
                                    onPick(c.country_code);
                                    setOpen(false);
                                    setQuery("");
                                }}
                                data-testid={`blog-country-pick-${c.country_code}`}
                                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[14px] transition hover:bg-[#F5F5F5]"
                            >
                                <span className="flex items-center gap-2">
                                    <span className="text-[18px] leading-none">{c.country_flag}</span>
                                    <span className="font-medium text-[#0A0A0A]">{c.country_name}</span>
                                </span>
                                <span className="text-[11px] text-[#A3A3A3]">
                                    {t("blog.post_count", { count: c.post_count })}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function PostCard({ post }) {
    return (
        <Link
            to={`/blog/${post.slug}`}
            data-testid={`blog-card-${post.slug}`}
            className="group flex flex-col overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white transition hover:-translate-y-0.5 hover:border-[#0A0A0A]"
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-[#F5F5F5]">
                {post.cover_image_url && (
                    <img
                        src={post.cover_image_url}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                )}
                <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-medium text-[#0A0A0A] backdrop-blur">
                    <span className="text-[14px] leading-none">{post.country_flag}</span>
                    {post.country_name}
                </span>
            </div>
            <div className="flex flex-1 flex-col gap-2 p-5">
                <h3 className="line-clamp-2 text-[18px] font-semibold leading-snug tracking-tight text-[#0A0A0A]">
                    {post.title}
                </h3>
                {post.excerpt && (
                    <p className="line-clamp-3 text-[13px] leading-relaxed text-[#737373]">
                        {post.excerpt}
                    </p>
                )}
                <div className="mt-auto flex items-center justify-between pt-3 text-[11px] text-[#A3A3A3]">
                    <span>{post.author_name || "Editorial"}</span>
                    <span>{timeAgo(post.published_at)}</span>
                </div>
            </div>
        </Link>
    );
}

export default function BlogIndexPage() {
    const navigate = useNavigate();
    const loc = useLocation();
    const { t, i18n } = useTranslation();
    const params = new URLSearchParams(loc.search);
    const country = params.get("country") || "";

    const [countries, setCountries] = useState([]);
    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const limit = 12;

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/blog/countries`)
            .then((r) => {
                if (!cancelled) setCountries(r.data || []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const qs = new URLSearchParams();
        if (country) qs.set("country", country);
        qs.set("limit", String(limit));
        qs.set("offset", "0");
        qs.set("lang", i18n.language?.split("-")[0] || "en");
        axios
            .get(`${API}/blog?${qs}`)
            .then((r) => {
                if (cancelled) return;
                setPosts(r.data?.posts || []);
                setTotal(r.data?.total || 0);
                setOffset(0);
            })
            .catch(() => {
                if (!cancelled) {
                    setPosts([]);
                    setTotal(0);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [country, i18n.language]);

    const loadMore = async () => {
        const nextOffset = offset + limit;
        const qs = new URLSearchParams();
        if (country) qs.set("country", country);
        qs.set("limit", String(limit));
        qs.set("offset", String(nextOffset));
        qs.set("lang", i18n.language?.split("-")[0] || "en");
        const r = await axios.get(`${API}/blog?${qs}`);
        setPosts((prev) => [...prev, ...(r.data?.posts || [])]);
        setOffset(nextOffset);
    };

    const setCountryParam = (cc) => {
        const next = new URLSearchParams(loc.search);
        if (cc) next.set("country", cc);
        else next.delete("country");
        navigate({ pathname: "/blog", search: next.toString() ? `?${next}` : "" });
    };

    return (
        <div className="min-h-screen bg-white text-[#0A0A0A]">
            <header className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white/90 backdrop-blur">
                <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2">
                        <HamburgerMenu />
                        <Logo size="sm" />
                    </div>
                    <div className="flex items-center gap-2">
                        <LanguageSwitcher compact />
                        <Link
                            to="/"
                            data-testid="blog-back-to-chat"
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[12px] text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                        >
                            <ArrowLeft size={12} strokeWidth={2.25} /> {t("nav.back_to_chat")}
                        </Link>
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full max-w-[1200px] px-5 py-12">
                <section data-testid="blog-hero" className="max-w-[820px]">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                        Piqpath · {t("blog.title")}
                    </div>
                    <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">
                        {t("blog.title")}
                    </h1>
                    <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#737373]">
                        {t("blog.lede")}
                    </p>
                </section>

                <div className="mt-8 max-w-[480px]">
                    <CountryAutocomplete
                        countries={countries}
                        value={country}
                        onPick={setCountryParam}
                        onClear={() => setCountryParam("")}
                    />
                </div>

                <section className="mt-10">
                    {loading ? (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="h-[340px] animate-pulse rounded-2xl border border-[#E5E5E5] bg-[#F5F5F5]" />
                            ))}
                        </div>
                    ) : posts.length === 0 ? (
                        <div
                            data-testid="blog-empty"
                            className="rounded-2xl border border-dashed border-[#E5E5E5] bg-white p-10 text-center"
                        >
                            <p className="text-[15px] text-[#737373]">
                                {t("blog.no_posts")}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div
                                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                                data-testid="blog-grid"
                            >
                                {posts.map((p) => (
                                    <PostCard key={p.id} post={p} />
                                ))}
                            </div>
                            {posts.length < total && (
                                <div className="mt-10 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={loadMore}
                                        data-testid="blog-load-more"
                                        className="inline-flex items-center gap-2 rounded-full border border-[#0A0A0A] bg-white px-5 py-2.5 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#0A0A0A] hover:text-white"
                                    >
                                        {t("blog.load_more")}
                                        <span className="text-[11px] text-[#A3A3A3] group-hover:text-white">
                                            {posts.length} / {total}
                                        </span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </main>

            <footer className="border-t border-[#E5E5E5] py-6 text-center text-[11px] text-[#A3A3A3]">
                Piqpath · Your trip. Your way.
            </footer>
        </div>
    );
}
